import { apiClient } from "@/lib/apiClient";
import type {
  SentMailListItem,
  SentMailListParams,
  SendMailPayload,
} from "../types/mail-broadcast.types";

/**
 * Dokümantasyon: docs/email-broadcast-backend-api.md
 * GET  /api/admin/email-broadcast
 * POST /api/admin/email-broadcast/send
 */
const BASE = "/api/admin/email-broadcast";

function mapSentMailRow(raw: Record<string, unknown>): SentMailListItem {
  return {
    id: String(raw.id ?? ""),
    sentAt: String(raw.sentAt ?? raw.sent_at ?? ""),
    recipientSummary: String(
      raw.recipientSummary ?? raw.recipient_summary ?? "",
    ),
    subject: String(raw.subject ?? ""),
    messagePreview: String(
      raw.messagePreview ?? raw.message_preview ?? "",
    ),
    status:
      raw.status != null && raw.status !== ""
        ? String(raw.status)
        : undefined,
  };
}

/** Backend’in { success, data, count } veya iç içe { data: { items, total } } kalıplarını destekler */
function normalizeSentListResponse(
  responseBody: unknown,
): { data: SentMailListItem[]; total: number } {
  if (!responseBody || typeof responseBody !== "object") {
    return { data: [], total: 0 };
  }
  const b = responseBody as Record<string, unknown>;
  const inner = b.data;

  let listRaw: unknown[] = [];
  let total = 0;

  if (Array.isArray(inner)) {
    listRaw = inner;
    total = Number(b.count ?? b.total ?? listRaw.length);
  } else if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const nested = inner as Record<string, unknown>;
    if (Array.isArray(nested.items)) {
      listRaw = nested.items;
      total = Number(
        nested.total ?? nested.count ?? b.count ?? b.total ?? listRaw.length,
      );
    } else if (Array.isArray(nested.data)) {
      listRaw = nested.data as unknown[];
      total = Number(
        nested.total ?? nested.count ?? b.count ?? b.total ?? listRaw.length,
      );
    }
  } else if (Array.isArray(b.items)) {
    listRaw = b.items;
    total = Number(b.total ?? b.count ?? listRaw.length);
  }

  const data = listRaw
    .filter((x): x is Record<string, unknown> => x != null && typeof x === "object")
    .map((x) => mapSentMailRow(x as Record<string, unknown>));

  return {
    data,
    total: Number.isFinite(total) && total > 0 ? total : data.length,
  };
}

export const mailBroadcastApi = {
  async getSentList(
    params?: SentMailListParams,
  ): Promise<{ data: SentMailListItem[]; total: number }> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 20;
    const query: Record<string, unknown> = { page, limit };
    if (params?.search?.trim()) query.search = params.search.trim();

    const { data: responseBody } = await apiClient.get<unknown>(BASE, {
      params: query,
    });
    return normalizeSentListResponse(responseBody);
  },

  async send(body: SendMailPayload): Promise<void> {
    await apiClient.post(`${BASE}/send`, body);
  },
};
